/**
 * Unified provision workspace — deploy + setup, one page, one conversation.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/state/language/LanguageProvider';
import {
  ASSIST_FORM_BODY_CLASS,
  ASSIST_PANE_SHELL_CLASS,
  ASSIST_PANE_HEADER_CLASS,
  ASSIST_PANE_TITLE_CLASS,
  ASSIST_SPLIT_GRID_CLASS,
  ASSIST_WORKSPACE_CONTAINER_CLASS,
  ASSIST_WORKSPACE_PAGE_CLASS,
} from '@/components/ai-studio/assist/assistPaneStyles';
import {
  ProvisionAssistPanel,
  ProvisionFormPanel,
  useProvisionAssist,
} from '@/components/ai-studio/provision';
import type { InstanceProvisionPatch } from '@/types/aiStudioAgent';
import { getInstance, listAgents } from '@/services/aiStudioAgentService';
import type { AiStudioAgent } from '@/types/aiStudioAgent';

const AIStudioProvisionInstancePage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { instanceId: routeInstanceId } = useParams<{ instanceId?: string }>();
  const [searchParams] = useSearchParams();
  const queryAgentId = searchParams.get('agentId') ?? undefined;

  const [mobilePane, setMobilePane] = useState<'chat' | 'form'>('chat');
  const [agents, setAgents] = useState<AiStudioAgent[]>([]);
  const [activeAgentId, setActiveAgentId] = useState(queryAgentId ?? '');
  const [instanceId, setInstanceId] = useState<string | null>(routeInstanceId ?? null);
  const [instanceName, setInstanceName] = useState<string | undefined>();
  const [suggestedPatch, setSuggestedPatch] = useState<InstanceProvisionPatch | null>(null);

  const assist = useProvisionAssist({
    agentId: instanceId ? undefined : activeAgentId || undefined,
    instanceId: instanceId ?? undefined,
    enabled: Boolean(instanceId || activeAgentId),
  });

  useEffect(() => {
    listAgents({ status: 'published', audience: 'catalog' })
      .then(setAgents)
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (queryAgentId) setActiveAgentId(queryAgentId);
  }, [queryAgentId]);

  useEffect(() => {
    if (routeInstanceId) {
      setInstanceId(routeInstanceId);
      getInstance(routeInstanceId)
        .then((instance) => {
          setInstanceName(instance.instanceName);
          if (instance.agentId) setActiveAgentId(instance.agentId);
        })
        .catch(() => undefined);
    }
  }, [routeInstanceId]);

  const scopeLabel = useMemo(
    () => instanceName ?? agents.find((a) => a.id === activeAgentId)?.name ?? t('aiStudio.instances.deployInstance'),
    [activeAgentId, agents, instanceName, t]
  );

  const handleInstanceCreated = useCallback(
    (newInstanceId: string) => {
      setInstanceId(newInstanceId);
      navigate(`/ai-studio/instances/${newInstanceId}/provision`, { replace: true });
      getInstance(newInstanceId)
        .then((instance) => setInstanceName(instance.instanceName))
        .catch(() => undefined);
      void assist.refreshSession();
    },
    [assist, navigate]
  );

  const handleApplyPatch = useCallback((patch: InstanceProvisionPatch) => {
    setSuggestedPatch(patch);
  }, []);

  const handleDraftSnapshot = useCallback(
    (draft: Parameters<typeof assist.setDraftSnapshot>[0]) => {
      assist.setDraftSnapshot({
        ...draft,
        agentId: activeAgentId,
        instanceId: instanceId ?? undefined,
      });
    },
    [activeAgentId, assist, instanceId]
  );

  const showAgentPicker = !instanceId && !routeInstanceId;

  return (
    <div className={ASSIST_WORKSPACE_PAGE_CLASS}>
      <div className={ASSIST_WORKSPACE_CONTAINER_CLASS}>
        <div className="mb-4 shrink-0">
          <nav className="mb-2 flex flex-wrap items-center gap-3 text-sm">
            <Link to="/ai-studio/instances" className="text-[var(--color-info)] underline">
              {t('aiStudio.instances.title')}
            </Link>
          </nav>
          <h1 className="text-xl font-bold text-[var(--color-surface-text)]">
            {t('aiStudio.provision.pageTitle')}
          </h1>
          <p className="text-sm text-[var(--color-secondary-text)]">
            {t('aiStudio.provision.pageSubtitle')}
          </p>
        </div>

        {showAgentPicker && (
          <div className="mb-3 shrink-0">
            <label className="mb-1 block text-base font-medium text-[var(--color-surface-text)]">
              {t('aiStudio.instances.form.agent')}
            </label>
            <select
              className="max-w-md rounded border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-3 py-2 text-base"
              value={activeAgentId}
              onChange={(e) => setActiveAgentId(e.target.value)}
            >
              <option value="">{t('aiStudio.common.none')}</option>
              {agents.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.name} ({agent.code})
                </option>
              ))}
            </select>
          </div>
        )}

        {activeAgentId || instanceId ? (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="mb-2 flex flex-shrink-0 items-center justify-end gap-2 px-1 lg:hidden">
              {mobilePane === 'form' ? (
                <button
                  type="button"
                  onClick={() => setMobilePane('chat')}
                  className="rounded-lg border border-[var(--color-border)] px-3 py-2 text-base"
                >
                  {t('aiStudio.provision.backToChat')}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setMobilePane('form')}
                  className="rounded-lg bg-[var(--color-primary-bg)] px-4 py-2.5 text-base font-medium text-[var(--color-primary-text)]"
                >
                  {t('aiStudio.provision.formPane')}
                </button>
              )}
            </div>

            <div className={`min-h-0 flex-1 overflow-hidden ${ASSIST_SPLIT_GRID_CLASS}`}>
              <div
                className={`${ASSIST_PANE_SHELL_CLASS} ${
                  mobilePane === 'chat' ? 'flex' : 'hidden'
                } lg:flex lg:h-full`}
              >
                <ProvisionAssistPanel
                  scopeLabel={scopeLabel}
                  assist={assist}
                  onApplyPatch={handleApplyPatch}
                />
              </div>

              <div
                className={`${ASSIST_PANE_SHELL_CLASS} ${
                  mobilePane === 'form' ? 'flex' : 'hidden'
                } lg:flex lg:h-full`}
              >
                <div className={ASSIST_PANE_HEADER_CLASS}>
                  <h2 className={ASSIST_PANE_TITLE_CLASS}>{t('aiStudio.provision.formPane')}</h2>
                </div>
                <div className={ASSIST_FORM_BODY_CLASS}>
                  <ProvisionFormPanel
                    agentId={activeAgentId}
                    instanceId={instanceId}
                    suggestedPatch={suggestedPatch}
                    onSuggestionApplied={() => setSuggestedPatch(null)}
                    onDraftSnapshot={handleDraftSnapshot}
                    onInstanceCreated={handleInstanceCreated}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-base text-[var(--color-surface-text-secondary)]">
            {t('aiStudio.instances.form.noAgents')}
          </p>
        )}
      </div>
    </div>
  );
};

export default AIStudioProvisionInstancePage;
